import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as admin from '../../api/admin.ts';
import { formatBytes, formatDateTime, formatRelative } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';
import { EmptyState } from '../common/EmptyState.tsx';
import { IconDownload } from '../../lib/icons.tsx';

export function ReleasesTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-releases'], queryFn: admin.releases });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin-releases'] });
    void queryClient.invalidateQueries({ queryKey: ['client-latest'] });
  };

  const setCurrent = useMutation({
    mutationFn: admin.setCurrentRelease,
    onSuccess: () => { toast.success('현재 버전으로 지정했습니다'); refresh(); },
    onError: (e: Error) => { toast.error(e); },
  });

  const remove = useMutation({
    mutationFn: admin.deleteRelease,
    onSuccess: () => { toast.info('릴리스를 목록에서 제거했습니다'); refresh(); },
    onError: (e: Error) => { toast.error(e); },
  });

  if (isLoading) return <span className="spinner" />;
  const items = data?.items ?? [];

  return (
    <>
      <section className="panel">
        <h2>배포 등록 방법</h2>
        <p className="panel__hint">
          클라이언트를 빌드한 뒤 아래 명령으로 등록하면 <code>/download</code> 페이지에 바로 나타납니다.
        </p>
        <code className="dl__code">
{`cd client && bash scripts/build.sh --version 1.0.0
bash scripts/package.sh --version 1.0.0
cd ../server && node scripts/publish-release.mjs ../client/dist`}
        </code>
      </section>

      {items.length === 0 && (
        <EmptyState
          icon={<IconDownload size={36} className="faint" />}
          title="등록된 배포본이 없습니다"
          description="위 명령으로 클라이언트를 등록하세요."
        />
      )}

      {items.map((release) => (
        <section key={release.version} className="panel">
          <div className="row" style={{ marginBottom: 8 }}>
            <h2 className="spacer">
              {release.version}
              {release.is_current && <span className="frow__badge" style={{ marginLeft: 8 }}>현재 버전</span>}
              <span className="frow__badge" style={{ marginLeft: 6 }}>{release.channel}</span>
            </h2>
            {!release.is_current && (
              <button
                className="btn btn--sm"
                onClick={() => { setCurrent.mutate(release.version); }}
              >
                현재 버전으로
              </button>
            )}
            <button
              className="btn btn--sm"
              onClick={() => { remove.mutate(release.version); }}
            >
              제거
            </button>
          </div>
          <p className="faint" style={{ fontSize: 12 }}>
            {formatDateTime(release.released_at)}
            {release.notes && ` · ${release.notes}`}
          </p>

          {release.artifacts.map((artifact) => (
            <div key={artifact.file} className="panel__row">
              <span className="spacer" style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13 }}>{artifact.label}</span>
                <div className="faint mono truncate" style={{ fontSize: 11 }}>{artifact.file}</div>
              </span>
              <span className="muted">{formatBytes(artifact.size)}</span>
              <a className="btn btn--sm" href={artifact.url} download>받기</a>
            </div>
          ))}
        </section>
      ))}

      {(data?.downloads ?? []).length > 0 && (
        <section className="panel">
          <h2>다운로드 통계</h2>
          {(data?.downloads ?? []).map((row) => (
            <div key={`${row.version}-${row.file}`} className="panel__row">
              <span className="spacer mono truncate" style={{ fontSize: 12 }}>{row.file}</span>
              <span className="muted">{row.count.toLocaleString()}회</span>
              <span className="faint" style={{ fontSize: 12 }}>{formatRelative(row.last_at)}</span>
            </div>
          ))}
        </section>
      )}
    </>
  );
}
